// @ts-nocheck
import React, { useState, useMemo } from "react";
import {
  Plus,
  Download,
  MoreHorizontal,
  Edit,
  Trash2,
  Zap,
  Info,
  Loader2,
  Upload,
  Scissors,
  Printer,
  Layers,
  PackageCheck,
  HelpCircle,
} from "lucide-react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  CompactKpiRibbon,
  type CompactKpiItem,
} from "@/components/shared/CompactKpiRibbon";
import {
  resolveProcessGroup,
  sortProcessesByGroupAndOrder,
  type ProcessGroupKey,
} from "./processGrouping";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  ListPageShell,
  useListFilter,
} from "@/components/shared/ListPageShell";
import {
  InlineFilterRow,
  FilterField,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import { cn, formatNumber, formatMoney } from "@/lib/utils";

const PROCESS_FILTER_FIELDS: FilterField[] = [
  CHECKBOX_COL_NULL,
  null,
  { key: "name", label: "Lọc công đoạn...", type: "text" },
  { key: "defaultMachineType", label: "Lọc loại máy...", type: "text" },
  { key: "defaultSpeed", label: "Lọc tốc độ...", type: "text" },
  { key: "defaultSetupTime", label: "Lọc TG setup...", type: "text" },
  { key: "defaultRunWastePct", label: "Lọc hao hụt...", type: "text" },
  { key: "defaultSetupWaste", label: "Lọc bù hao...", type: "text" },
  null,
  null,
];
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import type { Process } from "@/shared/schemas";
import { ProcessForm } from "./ProcessForm";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import { ImportDialog } from "@/components/shared/ImportDialog";
import { ProcessCategory, MachineType } from "@/config/status-registry";
import { useRowSelection } from "@/hooks/useRowSelection";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { useImportRefs } from "@/hooks/useImportRefs";
import { ROW_KEY, type ImportResult, type ImportRowResult } from "@/lib/import-kit";
import { extractErrorMessage } from "@/lib/utils";

const PROCESS_EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "stepId", label: "Mã CĐ", width: 14, required: true },
  { key: "name", label: "Tên Công đoạn", width: 25, required: true },
  { key: "description", label: "Mô tả", width: 30 },
  {
    key: "group",
    label: "Nhóm",
    width: 18,
    formatter: (_v, r: any) => {
      const g = resolveProcessGroup(r);
      return ProcessCategory[g]?.label || g;
    },
  },
  { key: "defaultMachineType", label: "Loại Máy Mặc Định", width: 20, ref: "machineType" },
  {
    key: "defaultSpeed",
    label: "Tốc độ chuẩn (SL/h)",
    width: 18,
    format: "number",
    formatter: (_v, r: any) => r.defaultSpeed || r.speed || 0,
  },
  {
    key: "defaultSetupTime",
    label: "TG Setup (phút)",
    width: 16,
    format: "number",
    formatter: (_v, r: any) => r.defaultSetupTime || 0,
  },
  { key: "unit", label: "Đơn vị", width: 12 },
  {
    key: "defaultRunWastePct",
    label: "Hao hụt chạy (%)",
    width: 16,
    format: "decimal",
    formatter: (_v, r: any) => r.defaultRunWastePct || r.wastePercent || 0,
  },
  {
    key: "defaultSetupWaste",
    label: "Hao hụt setup (bù hao)",
    width: 18,
    format: "number",
    formatter: (_v, r: any) => r.defaultSetupWaste || r.minWasteQty || 0,
  },
];

const PROCESS_IMPORT_COLUMNS = PROCESS_EXPORT_COLUMNS;

export default function ProcessLibraryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProcess, setEditingProcess] = useState<any>(null);
  const [selectedProcess, setSelectedProcess] = useState<any>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const urlGroup = useRouterState({ select: (s) => (s.location.search as any)?.group }) as string | undefined;
  const [categoryFilter, setCategoryFilter] = useState<string>(urlGroup || "ALL");
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();

  // Fetch processes từ API
  const { data, isLoading, error } = useQuery({
    queryKey: ["processes"],
    // Quay lại danh sách sau khi xoá/sửa ở trang chi tiết luôn tải lại, không hiện bản cũ (FB-7KL5H3)
    refetchOnMount: "always",
    queryFn: async () => {
      return (await trpcClient.technical.processes.list.query()) as Process[];
    },
  });

  // Mutation cho create/update
  const saveMutation = useMutation({
    mutationFn: async (processData: any) => {
      if (editingProcess) {
        const id = editingProcess.id || editingProcess.stepId;
        return await trpcClient.technical.processes.update.mutate({
          id,
          data: processData,
        });
      }
      return await trpcClient.technical.processes.create.mutate(processData);
    },
    onSuccess: async () => {
      setIsDialogOpen(false);
      setEditingProcess(null);
      await queryClient.invalidateQueries({ queryKey: ["processes"] });
    },
    onError: async (err: any) => {
      toast.error(err?.message || "Không thể lưu công đoạn");
    },
  });

  const allItems = data || [];

  // Sắp xếp danh sách công đoạn theo trình tự quy trình sản xuất ISO
  const sortedAllItems = useMemo(
    () => sortProcessesByGroupAndOrder(allItems),
    [allItems]
  );

  // Thống kê số lượng theo nhóm chuẩn hóa
  const catCounts: Record<string, number> = useMemo(() => {
    const counts: Record<string, number> = { ALL: allItems.length };
    for (const p of allItems) {
      const g = resolveProcessGroup(p);
      counts[g] = (counts[g] || 0) + 1;
    }
    return counts;
  }, [allItems]);

  const {
    paginatedItems: items,
    totalItems,
    currentPage,
    onPageChange,
    pageSize,
    onPageSizeChange,
    filterValues,
    onFilterChange,
    showFilter,
    onToggleFilter,
    onClearAll,
  } = useListFilter({
    data: sortedAllItems,
    searchFields: ["stepId", "name", "description"],
    preFilter: (item: any) =>
      categoryFilter === "ALL" || resolveProcessGroup(item) === categoryFilter,
  });

  // Cấu hình Dải Tags Ribbon (CompactKpiRibbon)
  const kpiItems: CompactKpiItem[] = useMemo(() => {
    const ribbonItems: CompactKpiItem[] = [
      {
        key: "ALL",
        label: "Tất cả",
        count: catCounts.ALL || 0,
        variant: "default",
        icon: Layers,
      },
      {
        key: "TRUOC_IN",
        label: "Trước in",
        count: catCounts.TRUOC_IN || 0,
        variant: "purple",
        icon: Scissors,
      },
      {
        key: "IN",
        label: "In ấn",
        count: catCounts.IN || 0,
        variant: "blue",
        icon: Printer,
      },
      {
        key: "SAU_IN",
        label: "Sau in",
        count: catCounts.SAU_IN || 0,
        variant: "amber",
        icon: Layers,
      },
      {
        key: "THANH_PHAM",
        label: "Thành phẩm",
        count: catCounts.THANH_PHAM || 0,
        variant: "emerald",
        icon: PackageCheck,
      },
    ];

    if ((catCounts.KHAC || 0) > 0) {
      ribbonItems.push({
        key: "KHAC",
        label: "Khác",
        count: catCounts.KHAC || 0,
        variant: "slate",
        icon: HelpCircle,
      });
    }

    return ribbonItems;
  }, [catCounts]);

  const rowIds = items.map((p: Process) => p.stepId);
  const rowSelection = useRowSelection(rowIds);
  const { selectedIds, selectionMode, clearSelection, selectedCount } =
    rowSelection;

  const { handleExport, isExporting } = useExcelExport({
    columns: PROCESS_EXPORT_COLUMNS,
    filename: "QuyTrinh_APV",
    localData: sortedAllItems,
    selectedIds,
    idKey: "stepId",
  });

  // Nhập Excel công đoạn (04/11/2026): mã CĐ đã có → cập nhật, mới → tạo; Loại máy khớp danh mục; báo cáo từng dòng
  const importRefs = useImportRefs();
  const processImportRefs = useMemo(() => ({ machineType: importRefs.machineType }), [importRefs]);
  const handleImport = async (validRows: Record<string, unknown>[]): Promise<ImportResult> => {
    const results: ImportRowResult[] = [];

    const existingMap = new Map<string, Process>();
    (data || []).forEach((p) => {
      if (p.stepId) {
        existingMap.set(p.stepId.toUpperCase().trim(), p);
      }
    });

    for (const row of validRows) {
      const stepId = String(row.stepId || "").trim();
      const rowNo = Number(row[ROW_KEY]) || 0;
      if (!stepId) continue;

      const groupKey = resolveProcessGroup({
        group: row.group ? String(row.group) : null,
        name: row.name ? String(row.name) : null,
        defaultMachineType: row.defaultMachineType ? String(row.defaultMachineType) : null,
        stepId,
      });

      const processPayload = {
        stepId,
        name: String(row.name || stepId).trim(),
        description: row.description ? String(row.description).trim() : null,
        group: groupKey,
        defaultMachineType: row.defaultMachineType ? String(row.defaultMachineType).trim() : null,
        unit: row.unit ? String(row.unit).trim() : null,
        defaultSpeed:
          row.defaultSpeed != null && !isNaN(Number(row.defaultSpeed))
            ? Number(row.defaultSpeed)
            : null,
        defaultSetupTime:
          row.defaultSetupTime != null && !isNaN(Number(row.defaultSetupTime))
            ? Number(row.defaultSetupTime)
            : null,
        defaultRunWastePct:
          row.defaultRunWastePct != null && !isNaN(Number(row.defaultRunWastePct))
            ? Number(row.defaultRunWastePct)
            : null,
        defaultSetupWaste:
          row.defaultSetupWaste != null && !isNaN(Number(row.defaultSetupWaste))
            ? Number(row.defaultSetupWaste)
            : null,
      };

      try {
        const existing = existingMap.get(stepId.toUpperCase());
        if (existing) {
          await trpcClient.technical.processes.update.mutate({
            id: (existing as any).id || existing.stepId,
            data: processPayload,
          });
          results.push({ row: rowNo, code: stepId, status: "updated" });
        } else {
          await trpcClient.technical.processes.create.mutate(processPayload);
          results.push({ row: rowNo, code: stepId, status: "created" });
        }
      } catch (err: any) {
        results.push({ row: rowNo, code: stepId, status: "failed", message: extractErrorMessage(err) || "Lỗi xử lý" });
      }
    }

    await queryClient.invalidateQueries({ queryKey: ["processes"] });
    return { rows: results };
  };


  const deleteMutation = useMutation({
    mutationFn: async (id: string) =>
      trpcClient.technical.processes.delete.mutate({ id }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["processes"] });
    },
  });

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [];
    actions.push({
      label: "Xuất Excel",
      icon: <Download className="w-3.5 h-3.5" />,
      onClick: () => {
        handleExport();
        clearSelection();
      },
    });
    if (hasPermission("technical", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) =>
              trpcClient.technical.processes.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "công đoạn",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["processes"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} công đoạn đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    return actions;
  };

  // Loading state
  if (isLoading) {
    return (
      <ListPageShell
        actions={<></>}
        currentPage={1}
        pageSize={20}
        totalItems={0}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
      >
        <Card className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          <span className="ml-2 text-slate-500">Đang tải dữ liệu...</span>
        </Card>
      </ListPageShell>
    );
  }

  // Error state
  if (error) {
    return (
      <ListPageShell
        actions={<></>}
        currentPage={1}
        pageSize={20}
        totalItems={0}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
      >
        <Card className="p-8 text-center text-red-500">
          Lỗi khi tải dữ liệu công đoạn. Vui lòng thử lại.
        </Card>
      </ListPageShell>
    );
  }

  return (
    <>
      <ListPageShell
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        filterValues={filterValues}
        filterFields={PROCESS_FILTER_FIELDS.filter((f): f is FilterField => Boolean(f && (f as any).key))}
        headerFilters={
          <CompactKpiRibbon
            items={kpiItems}
            activeKey={categoryFilter}
            onSelect={(key) => {
              setCategoryFilter(key || "ALL");
              onPageChange(1);
            }}
            testIdPrefix="process-group"
            className="h-8 py-0"
          />
        }
        actions={
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs border-slate-200 bg-white"
              onClick={() => setShowImport(true)}
            >
              <Upload className="w-3.5 h-3.5 mr-1.5 text-slate-500" /> Import
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs border-slate-200 bg-white"
              onClick={() => handleExport()}
              disabled={isExporting}
            >
              <Download className="w-3.5 h-3.5 mr-1.5 text-slate-500" /> Xuất Excel
            </Button>

          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) setEditingProcess(null);
            }}
          >
            {hasPermission("technical", "create") && (
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-3 h-3 mr-2" /> Thêm
                </Button>
              </DialogTrigger>
            )}
            <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingProcess
                    ? "Cập nhật Công đoạn"
                    : "Định nghĩa Công đoạn Mới"}
                </DialogTitle>
                <DialogDescription>
                  Thiết lập các thông số tiêu chuẩn cho quy trình sản xuất và
                  các kiểm soát định mức.
                </DialogDescription>
              </DialogHeader>
              <div className="py-2">
                <ProcessForm
                  defaultValues={editingProcess || undefined}
                  onSuccess={(formData) => saveMutation.mutate(formData)}
                  onCancel={() => setIsDialogOpen(false)}
                />
              </div>
            </DialogContent>
          </Dialog>
        </div>
      }
      currentPage={currentPage}
      pageSize={pageSize}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    >
      {/* Removed the inline tags as they are now in the Select dropdown inside actions */}
      <Card className="p-0 border-slate-200 shadow-sm overflow-hidden bg-white">
        <div className="overflow-x-auto apv-compact-table">
          <Table className={cn(selectionMode && "apv-select-mode")}>
            <TableHeader className="apv-table-header">
              <TableRow className="border-b-0">
                <DataTableSelectAllCheckbox
                  rowSelection={rowSelection}
                  className={cn("apv-col-checkbox w-10 text-center px-4")}
                />
                <TableHead className="w-[48px] text-center font-semibold text-[11px] uppercase py-3">
                  STT
                </TableHead>
                <TableHead className="min-w-[260px] font-semibold text-[11px] uppercase py-3">
                  Tên Công đoạn & Mô tả
                </TableHead>
                <TableHead className="w-[130px] font-semibold text-[11px] uppercase py-3">
                  Thiết bị mặc định
                </TableHead>
                <TableHead className="w-[100px] font-semibold text-center text-[11px] uppercase py-3">
                  Tốc độ chuẩn
                </TableHead>
                <TableHead className="w-[85px] font-semibold text-center text-[11px] uppercase py-3">
                  TG Setup
                </TableHead>
                <TableHead className="w-[80px] font-semibold text-center text-[11px] uppercase py-3">
                  Hao hụt%
                </TableHead>
                <TableHead className="w-[90px] font-semibold text-center text-[11px] uppercase py-3">
                  Bù hao setup
                </TableHead>
                <TableHead className="w-[140px] font-semibold text-[11px] uppercase py-3">
                  Máy liên quan
                </TableHead>
                <TableHead className="w-[50px] px-4"></TableHead>
              </TableRow>
              {showFilter && (
                <InlineFilterRow
                  columns={PROCESS_FILTER_FIELDS as any}
                  filterFields={PROCESS_FILTER_FIELDS.filter((f): f is FilterField => Boolean(f && (f as any).key))}
                  filterValues={filterValues}
                  onFilterChange={onFilterChange}
                />
              )}
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={10}
                    className="h-24 text-center text-slate-500"
                  >
                    Không tìm thấy công đoạn nào.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((p, index) => {
                  const currentGroup = resolveProcessGroup(p);
                  const prevGroup =
                    index > 0 ? resolveProcessGroup(items[index - 1]) : null;
                  const isNewGroup = currentGroup !== prevGroup;
                  const groupConfig = ProcessCategory[currentGroup] || {
                    label: currentGroup === "KHAC" ? "Khác" : currentGroup,
                    color: "bg-slate-50 text-slate-700",
                    dotColor: "bg-slate-400",
                  };
                  const stt = (currentPage - 1) * pageSize + index + 1;

                  return (
                    <React.Fragment key={p.stepId}>
                      {isNewGroup && (
                        <TableRow
                          key={`group-header-${currentGroup}`}
                          className="bg-slate-100/90 font-semibold text-xs border-y border-slate-200 select-none hover:bg-slate-100/90"
                        >
                          <TableCell colSpan={10} className="py-2.5 px-4">
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  "w-2.5 h-2.5 rounded-full",
                                  groupConfig.dotColor || "bg-slate-400"
                                )}
                              />
                              <span className="font-bold uppercase tracking-wider text-slate-800 text-[11px]">
                                {groupConfig.label}
                              </span>
                              <Badge
                                variant="secondary"
                                className="ml-1 text-[10px] h-4 px-1.5 font-normal bg-white/80 text-slate-600 border border-slate-200"
                              >
                                {catCounts[currentGroup] || 0} công đoạn
                              </Badge>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      <DataTableRow
                        id={p.stepId}
                        rowSelection={rowSelection}
                        className="h-[52px] hover:bg-slate-50/80 transition-colors border-b border-slate-100 last:border-0"
                        onClick={async () => {
                          setSelectedProcess(p);
                          setSheetOpen(true);
                        }}
                      >
                        <DataTableRowCheckbox
                          id={p.stepId}
                          rowSelection={rowSelection}
                          className={cn("px-4 apv-col-checkbox")}
                        />
                        <TableCell className="w-[48px] text-center font-mono text-[12px] font-bold text-slate-500 py-1">
                          {stt}
                        </TableCell>
                        <TableCell className="py-1">
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-900 leading-tight">
                              {p.name}
                            </span>
                            {p.description && p.description !== p.name && (
                              <span className="text-[11px] text-slate-400 line-clamp-1">
                                {p.description}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="py-1">
                          {p.defaultMachineType ? (
                            (() => {
                              const mInfo =
                                MachineType[
                                  p.defaultMachineType as keyof typeof MachineType
                                ];
                              return (
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    "border-transparent font-medium text-[11px]",
                                    mInfo?.color ||
                                      "bg-slate-50 text-slate-600 border-slate-200",
                                  )}
                                >
                                  {mInfo?.label ||
                                    p.defaultMachineType.replace("_", " ")}
                                </Badge>
                              );
                            })()
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center py-1">
                          <div className="flex flex-col items-center">
                            <span className="text-[13px] font-bold text-slate-800">
                              {formatNumber((p as any).defaultSpeed || p.speed)}
                            </span>
                            <span className="text-[10px] text-slate-400 uppercase tracking-tighter">
                              {p.unit || "tờ"}/h
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center py-1">
                          <Badge
                            variant="outline"
                            className="bg-slate-50 text-slate-700 border-slate-200 text-xs font-semibold"
                          >
                            {(p as any).defaultSetupTime !== null &&
                            (p as any).defaultSetupTime !== undefined
                              ? `${(p as any).defaultSetupTime} ph`
                              : (p as any).productionTime
                              ? `${(p as any).productionTime} ph`
                              : "—"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center py-1">
                          <Badge className="bg-amber-50 text-amber-700 border-none font-bold text-[11px]">
                            {(p as any).defaultRunWastePct !== null &&
                            (p as any).defaultRunWastePct !== undefined
                              ? `${(p as any).defaultRunWastePct}%`
                              : `${p.wastePercent || 0}%`}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center py-1">
                          <span className="text-xs font-medium text-slate-600">
                            {(p as any).defaultSetupWaste ??
                              (p as any).minWasteQty ??
                              "—"}
                          </span>
                        </TableCell>
                        <TableCell className="py-1">
                          <div className="flex flex-wrap gap-1">
                            {((p as any).machineProcesses || [])
                              .slice(0, 3)
                              .map((mp: any) => (
                                <Badge
                                  key={mp.machine?.id || mp.id}
                                  variant="outline"
                                  className="text-[9px] px-1 py-0 bg-blue-50 text-blue-600 border-blue-200"
                                >
                                  {mp.machine?.name
                                    ?.split(" ")
                                    .slice(0, 3)
                                    .join(" ") || "—"}
                                </Badge>
                              ))}
                            {((p as any).machineProcesses || []).length > 3 && (
                              <Badge
                                variant="outline"
                                className="text-[9px] px-1 py-0"
                              >
                                +{(p as any).machineProcesses.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell
                          className="px-4 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-slate-200/50"
                              >
                                <MoreHorizontal className="h-4 w-4 text-slate-400" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-[180px] shadow-lg border-slate-200"
                            >
                              <DropdownMenuLabel className="text-xs font-semibold text-slate-500">
                                Thiết lập công đoạn
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator className="bg-slate-100" />
                              {hasPermission("technical", "update") && (
                                <DropdownMenuItem
                                  className="text-[13px] py-2 cursor-pointer"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    setEditingProcess(p);
                                    setIsDialogOpen(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4 mr-2 text-slate-400" />{" "}
                                  Chỉnh sửa
                                </DropdownMenuItem>
                              )}
                              {hasPermission("technical", "delete") && (
                                <DropdownMenuItem
                                  className="text-[13px] py-2 text-rose-600 focus:text-rose-600 focus:bg-rose-50 cursor-pointer"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (
                                      await confirm(
                                        `Bạn có chắc muốn xóa quy trình ${p.name}?`,
                                      )
                                    ) {
                                      deleteMutation.mutate(p.stepId, {
                                        onError: (err: any) =>
                                          toast.error(
                                            `Không thể xóa: ${err.message}`,
                                          ),
                                      });
                                    }
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Gỡ bỏ
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </DataTableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />

      {/* Process Detail Dialog */}
      <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
        <DialogContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedProcess && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {selectedProcess.name}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="font-mono bg-slate-50">
                    {selectedProcess.stepId}
                  </Badge>
                  <Badge variant="outline" className="capitalize">
                    {MachineType[
                      (selectedProcess.defaultMachineType ||
                        "") as keyof typeof MachineType
                    ]?.label ||
                      selectedProcess.defaultMachineType?.replace("_", " ") ||
                      "—"}
                  </Badge>
                </div>
              </DialogHeader>
              <div className="space-y-5 py-5">
                {selectedProcess.description && (
                  <p className="text-sm text-muted-foreground">
                    {selectedProcess.description}
                  </p>
                )}
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Tốc độ chuẩn
                    </p>
                    <p className="text-lg font-bold">
                      {formatNumber(selectedProcess.speed)}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        {selectedProcess.unit}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Thời gian SX
                    </p>
                    <p className="text-lg font-bold">
                      {(selectedProcess as any).productionTime || "—"}{" "}
                      <span className="text-xs font-normal text-muted-foreground">
                        phút/1000
                      </span>
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Hao hụt</p>
                    <p className="text-lg font-bold text-amber-600">
                      {selectedProcess.wastePercent}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      SL hao hụt tối thiểu
                    </p>
                    <p className="text-lg font-bold">
                      {(selectedProcess as any).minWasteQty ?? "—"}
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Chi phí Setup
                    </p>
                    <p className="text-sm font-bold">
                      {formatMoney(selectedProcess.setupCost)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Chi phí đơn vị
                    </p>
                    <p className="text-sm font-bold">
                      {formatMoney(selectedProcess.unitCost)}
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      Cần khuôn bế
                    </p>
                    <p className="text-sm font-medium">
                      {selectedProcess.requiresDie ? "Có" : "Không"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Nhóm</p>
                    <p className="text-sm font-medium">
                      {ProcessCategory[
                        resolveProcessGroup(
                          selectedProcess,
                        ) as keyof typeof ProcessCategory
                      ]?.label ||
                        selectedProcess.group ||
                        "—"}
                    </p>
                  </div>
                </div>
                {((selectedProcess as any).machineProcesses || []).length >
                  0 && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">
                        Máy liên quan
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {(selectedProcess as any).machineProcesses.map(
                          (mp: any) => (
                            <Badge
                              key={mp.machine?.id || mp.id}
                              variant="outline"
                              className="text-xs px-2 py-1 bg-blue-50 text-blue-700"
                            >
                              {mp.machine?.name || "—"}
                            </Badge>
                          ),
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-2 pt-4 border-t justify-between items-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    setSheetOpen(false);
                    navigate({
                      to: "/processes/$processId",
                      params: { processId: selectedProcess.stepId },
                    });
                  }}
                >
                  <Info className="w-4 h-4 mr-1.5" /> Xem đầy đủ
                </Button>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      setSheetOpen(false);
                      setEditingProcess(selectedProcess);
                      setIsDialogOpen(true);
                    }}
                  >
                    <Edit className="w-4 h-4 mr-1.5" /> Sửa
                  </Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="destructive" size="sm" className="h-8">
                        <Trash2 className="w-4 h-4 mr-1.5" /> Xoá
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Xác nhận xoá quy trình</DialogTitle>
                        <DialogDescription>
                          Bạn có chắc chắn muốn xoá quy trình{" "}
                          <strong>{selectedProcess.name}</strong> không? Hành
                          động này không thể hoàn tác.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex justify-end gap-2 mt-4">
                        <DialogTrigger asChild>
                          <Button variant="outline">Huỷ</Button>
                        </DialogTrigger>
                        <Button
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={async () => {
                            deleteMutation.mutate(selectedProcess.stepId, {
                              onSuccess: () => {
                                setSheetOpen(false);
                              },
                              onError: (err: any) =>
                                toast.error(`Không thể xóa: ${err.message}`),
                            });
                          }}
                        >
                          Xoá
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ListPageShell>

    <ImportDialog
      open={showImport}
      onOpenChange={setShowImport}
      title="Quy trình Sản xuất"
      columns={PROCESS_IMPORT_COLUMNS}
      onImport={handleImport}
      refs={processImportRefs}
      onDownloadTemplate={() =>
        downloadExcelTemplate(PROCESS_IMPORT_COLUMNS, "QuyTrinh_APV")
      }
    />
  </>
  );
}
